

























import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { formatCategoryLabel } from "../../core/helpers/categoryLabelHelper";

// TypeScript 4.1 does not include these browser APIs in its DOM declarations.
// eslint-disable-next-line no-unused-vars
interface CategoryResizeObserver { observe(element: Element): void; disconnect(): void; }

@Component({})
export default class CategorySelector extends Vue {
  @Prop({ type: Array, default: () => [] }) items!: any[];
  @Prop({ default: null }) activeId!: any;
  @Prop({ type: String, default: "" }) moreButtonLabel!: string;
  @Prop({ type: String, default: "" }) emptyMoreButtonLabel!: string;
  visibleCount = 0;
  opened = false;
  query = "";
  dialogStyle: Record<string, string> = {};
  observer: CategoryResizeObserver | null = null;

  categoryLabel(value: unknown) { return formatCategoryLabel(value); }

  get defaultMoreLabel() { return this.moreButtonLabel || this.$t("View categories").toString(); }
  get emptyLabel() { return this.emptyMoreButtonLabel || this.defaultMoreLabel; }
  get moreLabel() { return this.visibleCount === 0 ? this.emptyLabel : this.defaultMoreLabel; }

  get visibleItems() { return this.items.slice(0, this.visibleCount); }
  get hasOverflow() { return this.visibleCount < this.items.length; }
  get activeInOverflow() { return this.activeId != null && !this.visibleItems.some(item => item.id === this.activeId); }
  get activeName() { const item = this.items.find(item => item.id === this.activeId); return item ? this.categoryLabel(item.name) : this.moreLabel; }
  get filteredItems() {
    const normalize = (value: string) => value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    const query = normalize(this.query.trim());
    return this.items.filter(item => normalize(String(item.name)).includes(query));
  }
  mounted() {
    // eslint-disable-next-line no-unused-vars
    const Observer = (window as unknown as { ResizeObserver?: new (callback: () => void) => CategoryResizeObserver }).ResizeObserver;
    if (Observer) {
      const observer = new Observer(() => this.measure());
      observer.observe(this.$refs.row as Element);
      this.observer = observer;
    }
    window.addEventListener("resize", this.measure);
    this.scheduleMeasure();
    const fonts = (document as unknown as { fonts?: { ready: Promise<unknown> } }).fonts;
    if (fonts) fonts.ready.then(() => this.scheduleMeasure());
  }
  beforeDestroy() {
    if (this.observer) this.observer.disconnect();
    window.removeEventListener("resize", this.measure);
  }
  @Watch("items", { deep: true })
  @Watch("activeId")
  @Watch("moreButtonLabel")
  @Watch("emptyMoreButtonLabel")
  @Watch("$i18n.locale")
  scheduleMeasure() { this.$nextTick(() => this.measure()); }
  measure() {
    const row = this.$refs.row as HTMLElement;
    const probe = this.$refs.measure as HTMLElement;
    if (!row || !probe || !row.clientWidth) return;
    const widths = Array.from(probe.children).map(el => (el as HTMLElement).getBoundingClientRect().width);
    const gap = 8;
    const allWidth = widths.slice(0, -3).reduce((sum, width) => sum + width, 0) + gap * Math.max(0, this.items.length - 1);
    if (allWidth <= row.clientWidth) { this.visibleCount = this.items.length; return; }
    let used = Math.max(...widths.slice(-3));
    let count = 0;
    while (count < this.items.length && used + widths[count] + gap <= row.clientWidth) {
      used += widths[count] + gap;
      count++;
    }
    if (this.visibleCount !== count) { this.visibleCount = count; this.scheduleMeasure(); }
  }
  open() {
    const trigger = this.$refs.trigger as HTMLElement;
    const rect = trigger.getBoundingClientRect();
    this.dialogStyle = window.innerWidth >= 768 ? {
      position: "fixed",
      top: `${Math.max(16, Math.min(rect.bottom + 8, window.innerHeight - 320))}px`,
      left: `${Math.max(16, Math.min(rect.left, window.innerWidth - 436))}px`,
      maxHeight: `${Math.max(200, window.innerHeight - Math.max(16, Math.min(rect.bottom + 8, window.innerHeight - 320)) - 16)}px`,
    } : {};
    this.opened = true;
    this.query = "";
    this.$nextTick(() => (this.$refs.search as HTMLElement).focus());
  }
  close() { this.opened = false; this.$nextTick(() => { const trigger = this.$refs.trigger as HTMLElement; if (trigger) trigger.focus(); }); }
  // The active category is cleared by pressing it again: no selection shows everything.
  toggle(item: any) { if (item.id === this.activeId) this.$emit("clear"); else this.$emit("select", item); }
  choose(item: any) { this.toggle(item); this.close(); }
  onDialogKeydown(event: KeyboardEvent) {
    if (event.key === "Escape") { event.preventDefault(); this.close(); }
    if (event.key !== "Tab") return;
    const dialog = this.$refs.dialog as HTMLElement;
    const nodes = Array.from(dialog.querySelectorAll<HTMLElement>("button, input"));
    const first = nodes[0], last = nodes[nodes.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
}
